"use client";

import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { useRouter } from "next/navigation";
import { useMemo } from "react";

/**
 * Program Cards 6002 — BDU auxiliary page "Explore Our Programs".
 *
 * Port of BDU v1 `components/AuxiliaryPage/OtherPrograms.tsx` + `organisms/ProgramCard.tsx`,
 * the strip every auxiliary page renders under the article when the page's
 * `show_other_program` flag is on. Completes the auxiliary page stack:
 * Banner 6000 -> Rich Text Editor 6001 -> Program Cards 6002.
 *
 * Distinct from Program Cards 1038 (Specialisations), which shares the card but
 * not the frame: this one sits on a tinted `#F5F5F5` band, centres its heading
 * only below 768px (left-aligned above), and lays the cards out as a horizontal
 * rail from 768px up instead of a centred wrapping grid.
 *
 * BREAKPOINTS — v1 `md:` (768px) is `tab:` here (this app remaps `md:` to 1230px).
 *
 * The reference's card tint carries an `opacity-63` class that is not a real Tailwind
 * utility and emits no rule in the live CSS, so the tint is the flat colour alone.
 * Its `scrollbar-hide` on the rail is likewise absent from the live stylesheet; the
 * scrollbar is hidden here the same way Program Cards 1045 does it.
 *
 * The heading is a `<p>` rather than a heading element, as in the reference — these
 * pages already carry their `<h1>` in Banner 6000 and their `<h2>`s in the article.
 */

interface CardRow {
  id?: string | number;
  meta_title?: string;
  /** Parent programme — set on specialisations, drives the two-line title */
  meta_parent_title?: string;
  meta_description?: string;
  meta_duration?: string;
  meta_image?: string;
  meta_alt_image?: string;
  meta_url?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface ProgramCards6002Data {
  metadata?: CardRow[];
  title?: string;
  title_text_color?: string;
  /** Band behind the whole section */
  desktop_background_color?: string;
  /** Rule under the heading */
  gradient_color_1?: string;
  gradient_color_2?: string;
  /** Card tint over the image */
  card_background_color?: string;
  cta_text?: string;
  status?: boolean;
}

/** Fixed in the reference: the specialisation accent colour. */
const ACCENT = "#F4F217";

/** The 16x10 arrow the reference pulls from its icon map. */
const KnowMoreArrow = () => (
  <svg
    width="16"
    height="10"
    viewBox="0 0 16 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M8.42542 5.59011C8.55821 5.45727 8.63281 5.27714 8.63281 5.08931C8.63281 4.90149 8.55821 4.72135 8.42542 4.58852L4.41838 0.58148C4.35304 0.513827 4.27488 0.459865 4.18846 0.422742C4.10204 0.385619 4.00909 0.366078 3.91504 0.365261C3.82099 0.364444 3.72771 0.382366 3.64066 0.417982C3.55361 0.453597 3.47452 0.506193 3.40802 0.572701C3.34151 0.639208 3.28891 0.718294 3.2533 0.805346C3.21768 0.892398 3.19976 0.985671 3.20058 1.07972C3.20139 1.17377 3.22093 1.26672 3.25806 1.35314C3.29518 1.43956 3.34914 1.51772 3.4168 1.58306L6.21471 4.38098L-3.16574 4.38098C-3.35361 4.38098 -3.53377 4.45561 -3.66661 4.58845C-3.79945 4.72128 -3.87408 4.90145 -3.87408 5.08931C-3.87408 5.27717 -3.79945 5.45734 -3.66661 5.59018C-3.53377 5.72302 -3.35361 5.79765 -3.16574 5.79765L6.21471 5.79765L3.4168 8.59556C3.28777 8.72916 3.21637 8.90808 3.21799 9.09381C3.2196 9.27953 3.29409 9.45719 3.42543 9.58852C3.55676 9.71985 3.73442 9.79434 3.92014 9.79596C4.10586 9.79757 4.28479 9.72618 4.41838 9.59715L8.42542 5.59011Z"
      fill="white"
    />
  </svg>
);

const ProgramCards6002 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as ProgramCards6002Data | undefined;
  const router = useRouter();

  const {
    title = "",
    title_text_color = "#222023",
    desktop_background_color = "#F5F5F5",
    gradient_color_1 = "#1F3D56",
    gradient_color_2 = "#7CB5E6",
    card_background_color = "#222023A1",
    cta_text = "Know more",
  } = sectionData || {};

  const cards = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.meta_status !== false)
      .sort(
        (a, b) =>
          (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  if (!sectionData?.status || cards.length === 0) return null;

  return (
    <div
      className="flex w-full flex-col gap-[14px] px-5 py-6 tab:gap-[46px] tab:px-10 tab:pb-12 tab:pt-5"
      style={{ backgroundColor: desktop_background_color }}
    >
      <div className="flex flex-col items-center justify-center gap-2 tab:items-start">
        <p
          className="text-[28px]/[36px] font-bold"
          style={{ color: title_text_color }}
        >
          {title}
        </p>
        <div
          className="h-[3px] w-[174px]"
          style={{
            backgroundImage: `linear-gradient(90deg, ${gradient_color_1} 0%, ${gradient_color_2} 100%)`,
          }}
        />
      </div>

      <div
        className={cn(
          "flex w-full flex-col flex-wrap items-center justify-center gap-3",
          "tab:flex-row tab:items-start tab:justify-start tab:gap-5",
          "tab:overflow-x-auto tab:[scrollbar-width:none] tab:[&::-webkit-scrollbar]:hidden",
        )}
      >
        {cards.map((card, index) => {
          const parent = (card.meta_parent_title || "").trim();
          const isSpecialisation = parent.length > 0;
          const headline = isSpecialisation ? parent : card.meta_title;

          return (
            <div
              key={card.id ?? index}
              onClick={() => {
                ctaClick({
                  section: "program_cards_6002",
                  component: "know_more",
                  label: cta_text,
                  step: "",
                  entity: card.meta_title || "",
                  parentEntity: parent,
                });
                if (card.meta_url) router.push(card.meta_url);
              }}
              aria-label={card.meta_alt_image || ""}
              className={cn(
                "relative flex min-h-[215px] w-[323px] cursor-pointer flex-col overflow-hidden rounded-[14px]",
                "px-5 pb-7 pt-[22px] text-white",
                "tab:h-[263px] tab:w-[243px]",
              )}
              style={{
                background: card.meta_image
                  ? `url(${card.meta_image}) no-repeat center center`
                  : undefined,
                backgroundSize: "cover",
              }}
            >
              <div
                className="absolute inset-0 z-0"
                style={{ backgroundColor: card_background_color }}
              />
              <div className="relative z-[1] flex h-full flex-1 flex-col items-start gap-8">
                <div className="flex w-full flex-col gap-2">
                  <p className="flex flex-col items-start gap-2 text-[32px]/[32px] font-bold tab:text-2xl/normal">
                    {headline}
                    {isSpecialisation && (
                      <span
                        className="flex text-[18px]/[24px] font-bold tab:hidden"
                        style={{ color: ACCENT }}
                      >
                        {card.meta_title}
                      </span>
                    )}
                  </p>
                  {isSpecialisation && (
                    <p
                      className="-mt-2 hidden text-[18px]/normal font-bold tab:flex"
                      style={{ color: ACCENT }}
                    >
                      {card.meta_title}
                    </p>
                  )}
                  <p className="text-xs font-bold">{card.meta_description}</p>
                  {card.meta_duration && (
                    <div className="mt-2 w-fit rounded bg-[#FAFAFA80] p-1.5 text-sm text-[#222023]">
                      <b>Duration: </b>
                      <span>{card.meta_duration}</span>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="mt-auto flex h-7 items-center justify-center gap-2 whitespace-nowrap rounded-md py-2 pl-0 pr-4 text-base font-bold transition-colors"
                >
                  <span>{cta_text}</span>
                  <KnowMoreArrow />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgramCards6002;
